import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Box,
  CopyButton,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { IconCopy, IconCornerDownRight } from '@/components/icons'
import { AppModal, PageCenterLoader } from '@/components/ui'
import { HighlightedSearchText } from '@/components/knowledge/HighlightedSearchText'
import { api } from '@/lib/api'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { TicketDetailMessage } from '@/types/api'

type Props = {
  opened: boolean
  onClose: () => void
  ticketId: string
  ticketLabel: string
  highlightQuery?: string | null
  onInsertToReply?: (text: string) => void
  onCopy?: () => void
}

function isCustomerMessage(m: TicketDetailMessage): boolean {
  return (m.message_type || '').toLowerCase() === 'customer'
}

function ReadonlyMessageRow({
  message,
  highlightQuery,
  onInsertToReply,
  onCopy,
}: {
  message: TicketDetailMessage
  highlightQuery?: string | null
  onInsertToReply?: (text: string) => void
  onCopy?: () => void
}) {
  const isCustomer = isCustomerMessage(message)
  const body = (message.content_translated || message.content || '').trim()
  const sender = isCustomer
    ? message.sender_name?.trim() || 'Клиент'
    : message.sender_name?.trim() || 'Оператор'
  const showOperatorActions = !isCustomer && !!body

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: isCustomer ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        maw="92%"
        p="sm"
        style={{
          borderRadius: 'var(--mantine-radius-md)',
          background: isCustomer
            ? 'var(--mantine-color-gray-light)'
            : 'var(--mantine-color-brand-light)',
          border: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Group gap={6} mb={4} wrap="nowrap" justify="space-between">
          <Group gap={6} wrap="wrap" style={{ minWidth: 0, flex: 1 }}>
            <Text size="xs" fw={600}>
              {sender}
            </Text>
            {message.created_at ? (
              <Text size="xs" c="dimmed">
                {formatDateTimeDot(message.created_at)}
              </Text>
            ) : null}
          </Group>
          {showOperatorActions ? (
            <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
              <CopyButton value={body}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Скопировано' : 'Копировать'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="xs"
                      aria-label="Копировать"
                      onClick={() => {
                        copy()
                        onCopy?.()
                      }}
                    >
                      <IconCopy size={13} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              {onInsertToReply ? (
                <Tooltip label="Добавить в ответ" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    aria-label="Добавить в ответ"
                    onClick={() => onInsertToReply(body)}
                  >
                    <IconCornerDownRight size={13} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          ) : null}
        </Group>
        {body ? (
          highlightQuery && highlightQuery.trim().length >= 2 ? (
            <HighlightedSearchText
              text={body}
              query={highlightQuery}
              size="sm"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            />
          ) : (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {body}
            </Text>
          )
        ) : (
          <Text size="sm" c="dimmed">
            (без текста)
          </Text>
        )}
      </Box>
    </Box>
  )
}

export function SimilarTicketThreadModal({
  opened,
  onClose,
  ticketId,
  ticketLabel,
  highlightQuery,
  onInsertToReply,
  onCopy,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState<TicketDetailMessage[]>([])

  useEffect(() => {
    if (!opened || !ticketId) return
    let cancelled = false
    setLoading(true)
    void api
      .ticketDetail(ticketId, { messagesLimit: 200, messagesOffset: 0 })
      .then((resp) => {
        if (cancelled) return
        const list = Array.isArray(resp.messages) ? resp.messages : []
        setMessages(
          list.filter(
            (m) =>
              !m.is_internal &&
              ['customer', 'operator'].includes((m.message_type || '').toLowerCase()),
          ),
        )
      })
      .catch(() => {
        if (!cancelled) setMessages([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, ticketId])

  useEffect(() => {
    if (!opened) setMessages([])
  }, [opened])

  const empty = !loading && messages.length === 0

  const title = useMemo(() => `Переписка · ${ticketLabel}`, [ticketLabel])

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={title}
      size="xl"
      keepMounted={false}
      zIndex={500}
    >
      <ScrollArea h="min(70vh, 640px)" offsetScrollbars type="auto">
        {loading ? (
          <PageCenterLoader />
        ) : empty ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Сообщений нет
          </Text>
        ) : (
          <Stack gap="sm" pr="xs">
            {messages.map((m) => (
              <ReadonlyMessageRow
                key={m.id}
                message={m}
                highlightQuery={highlightQuery}
                onInsertToReply={onInsertToReply}
                onCopy={onCopy}
              />
            ))}
          </Stack>
        )}
      </ScrollArea>
    </AppModal>
  )
}
